"use client";

import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Hand-drawn marks in the same voice as the "Studio" script in the logo:
 * a circle around a word, a rough underline, a small arrow, a signature.
 * Each is a wobbly SVG path that draws itself like a pen stroke the first
 * time it scrolls into view. Under reduced motion the marks render finished.
 *
 * The svg owns the single in-view trigger and the paths are variant children,
 * so a mark never depends on per-path observers. Stroke widths are in viewBox
 * units on purpose: vector-effect's non-scaling-stroke breaks pathLength dash
 * animation in Chrome, so each mark picks a width that lands around 2px at
 * its rendered size instead.
 */

const EASE = [0.65, 0, 0.35, 1] as const;

const pen = {
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

/** One variant per stroke: hidden until the svg scrolls in, then drawn after its delay */
const stroke = (delay: number, duration = 0.7) => ({
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration, ease: EASE, delay },
      opacity: { duration: 0.01, delay },
    },
  },
});

const trigger = (reduce: boolean) => ({
  initial: reduce ? undefined : ("hidden" as const),
  whileInView: "visible" as const,
  viewport: { once: true, margin: "-40px" },
});

interface MarkProps {
  /** Pen colour. Defaults to the surrounding text colour. */
  color?: string;
  /** Seconds to wait before the stroke starts */
  delay?: number;
  className?: string;
}

/** A double-pass marker underline */
export const UnderlinedWord = ({
  children,
  color = "currentColor",
  delay = 0,
  className,
}: MarkProps & { children: ReactNode }) => {
  const reduce = !!useReducedMotion();
  return (
    <span className={cn("relative inline-block", className)}>
      {children}
      <motion.svg
        aria-hidden
        viewBox="0 0 140 12"
        className="pointer-events-none absolute -bottom-[0.45em] left-0 w-full overflow-visible"
        {...trigger(reduce)}
      >
        <motion.path d="M3 8 C35 3, 82 2, 137 4" {...pen} stroke={color} strokeWidth={2.5} variants={stroke(delay, 0.55)} />
        <motion.path d="M9 11 C52 7, 98 6, 128 8" {...pen} stroke={color} strokeWidth={2} variants={stroke(delay + 0.35, 0.45)} />
      </motion.svg>
    </span>
  );
};

/** A small scrawled arrow, pointing down-right */
export const ArrowScribble = ({ color = "currentColor", delay = 0, className }: MarkProps) => {
  const reduce = !!useReducedMotion();
  return (
    <motion.svg aria-hidden viewBox="0 0 56 44" className={cn("overflow-visible", className)} {...trigger(reduce)}>
      <motion.path
        d="M4 4 C18 6, 30 14, 40 26 C43 30, 45 33, 47 37"
        {...pen}
        stroke={color}
        strokeWidth={3.5}
        variants={stroke(delay, 0.55)}
      />
      <motion.path
        d="M33 34 C38 36, 43 37, 47 37 C46 32, 46 27, 45 22"
        {...pen}
        stroke={color}
        strokeWidth={3.5}
        variants={stroke(delay + 0.5, 0.3)}
      />
    </motion.svg>
  );
};


